/* Lid-O — real CAD model (KeyShot 3MF → lido-L.glb via tools/3mf2glb.mjs) + scroll-driven story.
   Theme version: flat Shopify assets folder (imports next to this file), model URL passed in via opts.model
   (from Liquid data-model), dispose() for Theme Editor section unload, no idle re-render when nothing changed.
   CAD parts: cap (flip-top) · dial (knurled ring + LIDO arm) · lid (smoked body + spout = measuring chamber) · plate (seal diaphragm).
   Jar, powder and the red half-dose adapter are not in the CAD and stay procedural.
   1 unit = 10 mm (Large: 127 mm lid). */
import * as THREE from './three.module.min.js';
import { RoomEnvironment } from './RoomEnvironment.js';
import { loadGLB } from './glb-v2.js';

const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
const seg = (p, a, b) => clamp((p - a) / (b - a));
const ease = t => (t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const easeOut = t => 1 - Math.pow(1 - t, 3);
const back = t => { const c = 1.9; return 1 + (c + 1) * Math.pow(t - 1, 3) + c * Math.pow(t - 1, 2); };
const lerp = (a, b, t) => a + (b - a) * t;

function roundedRect(w, h, r) {
  const s = new THREE.Shape(), x = -w / 2, y = -h / 2;
  s.moveTo(x + r, y); s.lineTo(x + w - r, y); s.quadraticCurveTo(x + w, y, x + w, y + r);
  s.lineTo(x + w, y + h - r); s.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  s.lineTo(x + r, y + h); s.quadraticCurveTo(x, y + h, x, y + h - r);
  s.lineTo(x, y + r); s.quadraticCurveTo(x, y, x + r, y);
  return s;
}
function roundedPath(w, h, r) { const p = new THREE.Path(); p.setFromPoints(roundedRect(w, h, r).getPoints(24)); return p; }

export function mountLid(canvas, opts = {}) {
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, powerPreference: 'high-performance' });
  /* pixel budget: retina at 2× = 4 M px per frame → cap at 1.5 (1.25 on phones/tablets), and drop further if frames run slow */
  const coarse = matchMedia('(pointer: coarse)').matches;
  let dpr = opts.dpr || Math.min(window.devicePixelRatio, 1.5);
  renderer.setPixelRatio(dpr);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.12;

  const scene = new THREE.Scene();
  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
  scene.environmentIntensity = 0.72;                // stronger studio reflections read as hard plastic

  const camera = new THREE.PerspectiveCamera(30, 1, 0.1, 200);
  const camTarget = new THREE.Vector3(0, 1.5, 0);

  /* lights: soft key, cool fill, brand-red rim */
  const key = new THREE.DirectionalLight(0xffffff, 2.0); key.position.set(5, 14, 9); scene.add(key);
  const top = new THREE.DirectionalLight(0xffffff, 0.9); top.position.set(-2, 20, -2); scene.add(top);
  const fill = new THREE.DirectionalLight(0xbfd2ff, 0.5); fill.position.set(-8, 4, 6); scene.add(fill);
  const rim = new THREE.SpotLight(0xff2a3c, 90, 70, Math.PI / 6, 0.7, 1.4); rim.position.set(-9, 2.5, -13); scene.add(rim); rim.target.position.set(0, 1, 0); scene.add(rim.target);
  const rim2 = new THREE.SpotLight(0xffffff, 70, 70, Math.PI / 6, 0.8, 1.4); rim2.position.set(10, 4, -11); scene.add(rim2); rim2.target.position.set(0, 1, 0); scene.add(rim2.target);

  /* materials (CAD material groups: black · smoke · print · red) */
  /* grain: fine object-space texture on the normals + roughness (the CAD has no UVs), so the plastic reads moulded, not chrome */
  const grain = (mat, amp, freq) => {
    mat.onBeforeCompile = sh => {
      sh.vertexShader = sh.vertexShader.replace('#include <common>', '#include <common>\nvarying vec3 vGrainPos;').replace('#include <begin_vertex>', '#include <begin_vertex>\nvGrainPos = transformed;');
      sh.fragmentShader = sh.fragmentShader.replace('#include <common>', `#include <common>
varying vec3 vGrainPos;
float gH(vec3 p) { p = fract(p * 0.3183099 + 0.1); p *= 17.0; return fract(p.x * p.y * p.z * (p.x + p.y + p.z)); }
float gN(vec3 x) { vec3 i = floor(x), f = fract(x); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(mix(gH(i), gH(i + vec3(1,0,0)), f.x), mix(gH(i + vec3(0,1,0)), gH(i + vec3(1,1,0)), f.x), f.y),
             mix(mix(gH(i + vec3(0,0,1)), gH(i + vec3(1,0,1)), f.x), mix(gH(i + vec3(0,1,1)), gH(i + vec3(1,1,1)), f.x), f.y), f.z); }`)
        .replace('#include <roughnessmap_fragment>', `#include <roughnessmap_fragment>
roughnessFactor = clamp(roughnessFactor + (gN(vGrainPos * ${freq.toFixed(1)}) - 0.5) * 0.22, 0.0, 1.0);`)
        .replace('#include <normal_fragment_maps>', `#include <normal_fragment_maps>
{ vec3 q = vGrainPos * ${freq.toFixed(1)}; normal = normalize(normal + ${amp.toFixed(3)} * (vec3(gN(q), gN(q + 17.3), gN(q + 41.9)) - 0.5)); }`);
    };
    return mat;
  };
  const matte = grain(new THREE.MeshPhysicalMaterial({ color: 0x0b0b0d, roughness: 0.52, metalness: 0, specularIntensity: 0.55, clearcoat: 0.22, clearcoatRoughness: 0.38, ior: 1.49 }), 0.16, 90);   // moulded ABS/PP, light grain
  const smoke = grain(new THREE.MeshPhysicalMaterial({ color: 0x17171b, roughness: 0.2, metalness: 0, ior: 1.49, clearcoat: 0.45, clearcoatRoughness: 0.22, specularIntensity: 0.8, transparent: true, opacity: 0.86, side: THREE.DoubleSide }), 0.06, 90);   // smoked polycarbonate, satin
  const print = new THREE.MeshStandardMaterial({ color: 0xd2d2d6, roughness: 0.55, polygonOffset: true, polygonOffsetFactor: -2 });
  const red = new THREE.MeshStandardMaterial({ color: 0xe0142e, roughness: 0.45, emissive: 0x3a0006, polygonOffset: true, polygonOffsetFactor: -2 });
  const ruby = new THREE.MeshPhysicalMaterial({ color: 0xc8122a, roughness: 0.12, metalness: 0, ior: 1.5, transparent: true, opacity: 0, emissive: 0x3a0006, emissiveIntensity: 0.25 });
  const jarMat = new THREE.MeshPhysicalMaterial({ color: 0x0a0a0b, roughness: 0.86, metalness: 0, sheen: 0.4, sheenRoughness: 0.8, sheenColor: new THREE.Color(0x333338) });
  const powderMat = new THREE.MeshStandardMaterial({ color: 0xe2bd93, roughness: 0.9, emissive: 0x5a4430, emissiveIntensity: 0.9 });

  const model = new THREE.Group(); scene.add(model);
  /* real CAD (Large, Ø127 mm) is rotated 180° so the spout sits behind the hub and the LIDO arm faces front-left */
  const SZ = -3.06, Y0 = -0.55;           // spout centre (z) · lid underside sits on the jar rim
  const parts = {};

  /* 10 · jar */
  const jarPts = [];
  const J = [[0, -17], [6.1, -17], [6.9, -16.3], [7.15, -14.8], [7.15, -2.8], [6.95, -1.5], [6.4, -0.75], [6.1, -0.55], [6.1, 0.2]];
  J.forEach(([x, y]) => jarPts.push(new THREE.Vector2(x * 1.02, y)));
  parts.jar = new THREE.Mesh(new THREE.LatheGeometry(new THREE.SplineCurve(jarPts).getPoints(80), 96), jarMat);
  model.add(parts.jar);

  /* CAD parts are filled in when the GLB arrives; empty groups keep the timeline working meanwhile */
  const cad = new THREE.Group(); cad.rotation.y = Math.PI; cad.position.y = Y0; cad.visible = false; model.add(cad);
  parts.lid = new THREE.Group(); parts.dial = new THREE.Group(); parts.diaphragm = new THREE.Group();
  parts.cap = new THREE.Group();                                   // hinge pivot (CAD frame: hinge strap at z ≈ 0.42, y ≈ 6.58)
  parts.cap.position.set(0, 6.58, 0.42);
  cad.add(parts.diaphragm, parts.lid, parts.dial, parts.cap);

  /* powder fills the measuring chamber = the spout itself (inner ≈ 44 × 35 mm) */
  const powder = new THREE.Mesh(new THREE.ExtrudeGeometry(roundedRect(4.25, 3.35, 0.95), { depth: 1, bevelEnabled: false, curveSegments: 20 }), powderMat);
  powder.rotation.x = -Math.PI / 2; powder.position.set(0, 2.1, SZ); powder.scale.set(1, 1, 0.001); model.add(powder);

  /* powder stream: grains pouring from the jar through the opened seal into the chamber (model frame, upright coords) */
  const GRAINS = 220;
  const grains = new THREE.InstancedMesh(new THREE.IcosahedronGeometry(0.09, 0), powderMat, GRAINS);
  const grainSeed = Array.from({ length: GRAINS }, () => [Math.random(), (Math.random() - 0.5) * 3.2, (Math.random() - 0.5) * 2.4, 0.6 + Math.random() * 0.8]);
  grains.visible = false; grains.frustumCulled = false; model.add(grains);
  const _m = new THREE.Matrix4(), _q = new THREE.Quaternion(), _s = new THREE.Vector3(), _p = new THREE.Vector3();
  let streaming = 0, clock = 0;
  function pourGrains(strength, surfaceY) {
    streaming = strength;
    grains.visible = strength > 0.01;
    if (!grains.visible) return;
    const y0 = -1.2, span = Math.max(0.2, surfaceY - y0);
    grainSeed.forEach(([ph, x, z, sp], i) => {
      const t = (ph + clock * sp * 0.9) % 1;
      const narrow = 0.35 + 0.65 * t;                       // stream spreads as it falls into the chamber
      _p.set(x * narrow, y0 + t * span, SZ + z * narrow);
      const sc = strength * (t > 0.96 ? (1 - t) / 0.04 : 1);
      _s.setScalar(Math.max(0.001, sc));
      _m.compose(_p, _q, _s); grains.setMatrixAt(i, _m);
    });
    grains.instanceMatrix.needsUpdate = true;
  }

  /* red half-dose adapter: an optional insert that clips into the chamber */
  parts.chamber = new THREE.Group();
  const cupShape = roundedRect(4.15, 3.25, 0.92); cupShape.holes.push(roundedPath(3.85, 2.95, 0.78));
  const cup = new THREE.Mesh(new THREE.ExtrudeGeometry(cupShape, { depth: 3.2, bevelEnabled: false, curveSegments: 24 }), ruby);
  cup.rotation.x = -Math.PI / 2; cup.position.y = 2.2; parts.chamber.add(cup);
  parts.chamber.position.set(0, 0, SZ); model.add(parts.chamber);

  /* assembled (rest) pose of every part — callout anchors are bound in this pose, whatever the scroll position */
  const REST = Object.fromEntries(Object.entries(parts).map(([k, o]) => [k, [o.position.clone(), o.rotation.clone()]]));

  const mats = { black: matte, smoke, print, red };
  const strap = [];
  const PIV = new THREE.Vector3(0, 6.58, 0.42);                    // hinge pivot in the CAD frame
  function bendStrap(exHide) {
    const C = parts.cap.position, th = parts.cap.rotation.x, L = parts.lid.position;
    for (const s of strap) {
      const P = s.mesh.geometry.attributes.position, N = s.mesh.geometry.attributes.normal;
      for (let i = 0, n = s.w.length; i < n; i++) {
        const w = s.w[i] + (1 - s.w[i]) * exHide, a = w * th, c = Math.cos(a), si = Math.sin(a);
        const y = s.pos[i * 3 + 1] - PIV.y, z = s.pos[i * 3 + 2] - PIV.z;
        P.array[i * 3] = s.pos[i * 3] + L.x + (C.x - PIV.x - L.x) * w;
        P.array[i * 3 + 1] = PIV.y + y * c - z * si + L.y + (C.y - PIV.y - L.y) * w;
        P.array[i * 3 + 2] = PIV.z + y * si + z * c + L.z + (C.z - PIV.z - L.z) * w;
        const ny = s.nor[i * 3 + 1], nz = s.nor[i * 3 + 2];
        N.array[i * 3 + 1] = ny * c - nz * si; N.array[i * 3 + 2] = ny * si + nz * c;
      }
      P.needsUpdate = true; N.needsUpdate = true;
    }
  }
  if (!opts.model) throw new Error('Lid-O: opts.model (GLB url) is required');
  loadGLB(opts.model, mats).then(root => {
    const byName = n => root.children.find(c => c.name === n);
    [['lid', parts.lid], ['dial', parts.dial], ['plate', parts.diaphragm]].forEach(([n, g]) => { const src = byName(n); if (src) [...src.children].forEach(c => g.add(c)); });
    const cap = byName('cap');
    if (cap) [...cap.children].forEach(c => { c.position.set(0, -6.58, -0.42); parts.cap.add(c); });
    /* hinge strap: vertices are blended between the band (weight 0) and the flipping top (weight 1) every frame */
    const st = byName('strap');
    if (st) [...st.children].forEach(m => {
      const gp = m.geometry;
      strap.push({ mesh: m, pos: gp.attributes.position.array.slice(), nor: gp.attributes.normal.array.slice(), w: gp.attributes.weight.array });
      m.frustumCulled = false; cad.add(m);
    });
    parts.lid.traverse(o => { if (o.isMesh && o.userData.material === 'smoke') o.renderOrder = 2; });
    /* the seal plate is an open CAD surface (single-sided, faces the jar) → render both sides */
    const plateMat = matte.clone(); plateMat.side = THREE.DoubleSide;
    parts.diaphragm.traverse(o => { if (o.isMesh) o.material = plateMat; });
    cad.visible = true; bindAnchors(); needs = true; draw();          // show the model right away, even if no frame is pending
    if (opts.onReady) opts.onReady();
  }).catch(err => console.error('Lid-O model', err));

  /* soft contact shadow */
  const sc = document.createElement('canvas'); sc.width = sc.height = 256;
  const g = sc.getContext('2d'), grd = g.createRadialGradient(128, 128, 0, 128, 128, 128);
  grd.addColorStop(0, 'rgba(0,0,0,.75)'); grd.addColorStop(0.55, 'rgba(0,0,0,.3)'); grd.addColorStop(1, 'rgba(0,0,0,0)');
  g.fillStyle = grd; g.fillRect(0, 0, 256, 256);
  const shadow = new THREE.Mesh(new THREE.PlaneGeometry(22, 22), new THREE.MeshBasicMaterial({ map: new THREE.CanvasTexture(sc), transparent: true, depthWrite: false }));
  shadow.rotation.x = -Math.PI / 2; shadow.position.y = -17.05; model.add(shadow);

  /* callout anchors: model-space points at rest, re-expressed in each part's own frame so they follow it */
  const anchorsAtRest = {
    cap: [new THREE.Vector3(2.35, 7.15, SZ), 'cap'], chamber: [new THREE.Vector3(2.45, 3.0, SZ), 'lid'], adapter: [new THREE.Vector3(0, 5.7, SZ), 'chamber'],
    dial: [new THREE.Vector3(6.6, 0.9, 0), 'dial'], lid: [new THREE.Vector3(-4.9, 2.0, 1.2), 'lid'], diaphragm: [new THREE.Vector3(-2.3, 1.3, -2.2), 'diaphragm'],
  };
  const anchors = {};
  function bindAnchors() {
    /* the model may arrive while the view is already exploded: bind in the rest pose, then restore */
    const rot = [model.rotation.x, model.rotation.y, model.rotation.z];
    const now = Object.fromEntries(Object.keys(REST).map(k => [k, [parts[k].position.clone(), parts[k].rotation.clone()]]));
    for (const [k, [p, r]] of Object.entries(REST)) { parts[k].position.copy(p); parts[k].rotation.copy(r); }
    model.rotation.set(0, 0, 0); model.updateMatrixWorld(true);
    for (const [k, [p, part]] of Object.entries(anchorsAtRest)) anchors[k] = [parts[part].worldToLocal(model.localToWorld(p.clone())), parts[part]];
    for (const [k, [p, r]] of Object.entries(now)) { parts[k].position.copy(p); parts[k].rotation.copy(r); }
    model.rotation.set(rot[0], rot[1], rot[2]); model.updateMatrixWorld(true);
  }

  /* ------------------------------------------------------------ timeline */
  let progress = 0, target = 0, needs = true, pointerX = 0, pointerY = 0, idle = 0;
  const base = { cap: parts.cap.position.y, capZ: parts.cap.position.z };
  const flash = new THREE.PointLight(0xff2a3c, 0, 14, 2); flash.position.set(0, 3, 0); model.add(flash);

  /* story (p = scroll progress) — follows real use:
     0–.18 intro · .18–.36 explode + callouts · .40–.52 parts return WHILE the jar turns upside down (.42–.54)
     .55–.62 lever to the right → seal OPENS · .58–.73 powder pours from the jar into the chamber
     .73–.79 lever back → seal closes (click) · .79–.87 jar turned back · .88–.97 flip-top opens · ready */
  const TURN = 2.0;                                               // lever travel (rad): rest → right-hand stop
  const FULL = 3.3;                                               // powder height in the chamber when full
  function apply(p) {
    const intro = ease(seg(p, 0, 0.18));
    const ex = ease(seg(p, 0.18, 0.36)) * (1 - ease(seg(p, 0.4, 0.52)));
    const inv = ease(seg(p, 0.42, 0.54)) * (1 - ease(seg(p, 0.79, 0.87)));
    const upBack = ease(seg(p, 0.79, 0.87));
    const side = ease(seg(p, 0.42, 0.56));
    const open = ease(seg(p, 0.55, 0.62)), close = seg(p, 0.73, 0.79);
    const fillT = easeOut(seg(p, 0.6, 0.73));
    const pour = clamp(seg(p, 0.58, 0.61) * (1 - seg(p, 0.71, 0.74)));
    const outro = ease(seg(p, 0.86, 1));
    const capOpen = ease(seg(p, 0.88, 0.97));

    /* flip-top swings on its short hinge strap (no sliding) until it leans upside-down on the deck beside the spout */
    const flip = ease(capOpen);
    /* exploded view: the dial lifts off, the lid body drops out from UNDER it; the flip-top stays on its hinge
       (it rides with the lid body and swings half open instead of floating away) */
    const LID_DROP = 2.4, DIAL_LIFT = 14.2, CAP_EX = 1.75;   // dial lifts well clear: the hinged flip-top sits free between dial and lid body
    parts.lid.position.y = -ex * LID_DROP;
    parts.cap.position.y = base.cap + parts.lid.position.y;
    parts.cap.position.z = base.capZ;
    parts.cap.rotation.x = -ex * CAP_EX - flip * 3.78;
    parts.chamber.position.y = ex * 3.4;
    parts.chamber.position.x = -ex * 7.8;
    parts.chamber.rotation.z = ex * 0.28;
    ruby.opacity = clamp(ex * 1.4) * 0.82;               // no transmission pass: plain translucent red
    parts.chamber.visible = ex > 0.02;
    parts.dial.position.y = ex * DIAL_LIFT;
    parts.diaphragm.position.y = -ex * 10.4;
    parts.diaphragm.position.z = -ex * 6.4;            // CAD frame is rotated 180°: −z = towards the viewer
    parts.jar.position.y = -ex * 15;
    bendStrap(0);                                      // the strap always bends between band and flip-top

    /* lever: right = open, back = closed with a click + small overshoot */
    const cl = close < 0.85 ? ease(close / 0.85) : 1;
    const settle = close >= 0.85 ? back(clamp((close - 0.85) / 0.15)) : 0;
    parts.dial.rotation.y = TURN * (open - cl) - (close >= 0.85 ? 0.04 * (1 - settle) : 0);
    parts.diaphragm.rotation.y = parts.dial.rotation.y * 0.9;
    const clickPulse = Math.max(0, 1 - Math.abs(close - 0.86) * 9);
    flash.intensity = 0;                               // no red flash on the click (Emre 06.10)
    ruby.emissiveIntensity = 0.35;
    smoke.emissive = smoke.emissive || new THREE.Color(0); smoke.emissive.setRGB(0, 0, 0);

    /* powder: upside down it piles up at the cap end of the chamber; turned back it rests on the closed seal */
    const h = fillT * FULL;
    powder.scale.z = Math.max(0.001, h);
    powder.position.y = lerp(5.55 - h, 2.1, upBack) - ex * LID_DROP;
    powder.visible = cad.visible && fillT > 0.002;
    pourGrains(cad.visible ? pour : 0, 5.55 - h);

    /* model pose: flip about the jar's side axis; spout seen side-on; open cap lands on the right */
    model.rotation.z = inv * Math.PI;
    model.rotation.y = lerp(-1.1, 0, intro) + ex * 0.4 + side * 1.35 + outro * 0.2 + pointerX * 0.08;
    model.rotation.x = lerp(0.08, 0.0, intro) + pointerY * 0.04;
    shadow.visible = inv < 0.02;

    /* camera: low hero → three-quarter → pulled back while exploded → below the lid while it hangs upside down */
    const wide = camera.aspect > 1.1 ? 1 : 0;                         // desktop: page opens with the lid on the right, clear of the headline
    const dist = lerp(34 + wide * 5, 44, intro) + ex * 31 - side * 9 + inv * 3 + outro * 2;
    const elev = lerp(0.75, 0.6, intro) - ex * 0.22 - side * 0.3 - inv * 0.85 + outro * 0.42;
    const d2 = dist * (camera.aspect < 0.8 ? 1.32 : 1);
    camera.position.set(0, Math.sin(elev) * d2, Math.cos(elev) * d2);
    camTarget.set(-side * 1.6 * (1 - outro * 0.6) * (1 - inv) - wide * 2.8 * (1 - intro), lerp(2.2, 0.6, intro) + ex * 4.4 + side * 3.2 - outro * 0.8 - inv * 4.4, 0);
    camera.lookAt(camTarget);
    return { intro, ex, turn: close, fillT, outro };
  }

  const _v = new THREE.Vector3();
  function project(name) {
    const a = anchors[name];
    if (!a) return { x: -1, y: -1 };
    _v.copy(a[0]); a[1].localToWorld(_v); _v.project(camera);
    return { x: (_v.x * 0.5 + 0.5), y: (-_v.y * 0.5 + 0.5) };
  }

  let cw = 0, ch = 0;
  function resize() {
    const r = canvas.getBoundingClientRect();
    /* iOS fires resize whenever the toolbar slides; re-assigning canvas.width throws away the GPU buffers → only on a real change */
    if (Math.abs(r.width - cw) < 1 && Math.abs(r.height - ch) < 1 && renderer.getPixelRatio() === dpr) return;
    cw = r.width; ch = r.height;
    renderer.setSize(r.width, r.height, false);
    camera.aspect = r.width / r.height;
    camera.fov = camera.aspect < 0.8 ? 40 : 30;
    /* frame the model next to the copy: right half on desktop, upper half on phones */
    if (opts.frame !== false) {
      if (camera.aspect > 1.1) camera.setViewOffset(r.width, r.height, -r.width * 0.17, 0, r.width, r.height);
      else camera.setViewOffset(r.width, r.height, 0, r.height * 0.16, r.width, r.height);
    }
    camera.updateProjectionMatrix();
    needs = true;
  }
  function invalidate() { needs = true; }

  let raf = 0, visible = true, state = {}, idleTick = 0, disposed = false;
  let prevT = 0, drewPrev = false;
  function frame(t) {
    raf = requestAnimationFrame(frame);
    const gap = t - prevT; prevT = t;
    if (!visible) { drewPrev = false; return; }
    /* scroll events arrive unevenly on touch (momentum, 60/120 Hz mix) → glide towards the scroll position instead of jumping to it */
    const d = target - progress;
    if (Math.abs(d) > 0.0003) { progress += d * (1 - Math.exp(-Math.min(gap, 50) / 70)); needs = true; }
    else if (d !== 0) { progress = target; needs = true; }
    if (streaming > 0.01) { clock += 1 / 60; needs = true; }   // keep the powder stream moving while it pours
    if (progress < 0.001) { idle += 0.004; idleTick = (idleTick + 1) % 3; if (idleTick === 0) needs = true; } // gentle idle sway at ~20 fps
    if (!needs) { drewPrev = false; return; }
    if (drewPrev) adapt(gap);                           // only back-to-back draws (scrolling) say anything about GPU load
    draw(); drewPrev = true;
  }
  function draw() {
    state = apply(progress);
    if (progress < 0.001) { model.rotation.y += Math.sin(idle) * 0.02; }
    renderer.render(scene, camera);
    needs = false;
    if (opts.onFrame) opts.onFrame(state, project);
  }
  /* adaptive quality: weak GPUs (older iPhones, Intel laptops) step the pixel ratio down instead of stuttering */
  let slow = 0;
  function adapt(gap) {
    if (opts.dpr || !(gap < 200) || dpr <= 1) return;                // ignore tab switches; 1× is the floor
    slow = gap > 22 ? slow + 1 : Math.max(0, slow - 1);  // < ~45 fps while scrolling
    if (slow > 12) { slow = 0; dpr = Math.max(1, dpr - 0.25); renderer.setPixelRatio(dpr); resize(); }
  }
  resize(); requestAnimationFrame(frame);
  window.addEventListener('resize', resize);
  const io = new IntersectionObserver(e => { visible = e[0].isIntersecting; if (visible) needs = true; }); io.observe(canvas);

  function dispose() {
    if (disposed) return; disposed = true;
    cancelAnimationFrame(raf); io.disconnect(); window.removeEventListener('resize', resize);
    scene.traverse(o => { if (o.isMesh) { o.geometry?.dispose(); const m = o.material; (Array.isArray(m) ? m : [m]).forEach(x => x && x.dispose && x.dispose()); } });
    shadow.material.map?.dispose(); pmrem.dispose(); renderer.dispose();
    renderer.forceContextLoss && renderer.forceContextLoss();
  }

  return {
    setProgress(p) { if (disposed) return; target = clamp(p); if (!cad.visible) progress = target; },
    setPointer(x, y) { if (disposed) return; pointerX = x; pointerY = y; needs = true; },
    invalidate,
    /* offline capture (tools/render-seq.mjs): draw one exact story position, hand back the callout projector */
    renderAt(p) { progress = target = clamp(p); clock = progress * 40; draw(); return { state, project }; },
    dispose,
  };
}
